import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Eye, UserCheck, AlertTriangle, CheckCircle, Clock, ArrowUp, Filter, Users, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import ExceptionQueueService from "@/api/exceptionQueue.service";
import UsersService from "@/api/users.service";

interface ExceptionRecord {
  id: number;
  publicId: string;
  businessId: number;
  documentUploadId?: number;
  exceptionType: 'low_confidence' | 'validation_failed' | 'duplicate' | 'amount_threshold' | 'extraction_failed';
  exceptionCategory: 'extraction' | 'classification' | 'validation' | 'posting';
  description?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_review' | 'resolved' | 'escalated';
  assignedTo?: number;
  assignedAt?: string;
  resolvedBy?: number;
  resolvedAt?: string;
  resolutionNotes?: string;
  dateCreated: string;
  documentUpload?: {
    id: number;
    fileName: string;
    originalFileName: string;
    processingStatus: string;
  };
  assignedUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  resolvedUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export default function ExceptionQueue() {
  const [exceptions, setExceptions] = useState<ExceptionRecord[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [selectedException, setSelectedException] = useState<ExceptionRecord | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
  const [isEscalateDialogOpen, setIsEscalateDialogOpen] = useState(false);
  const [selectedExceptions, setSelectedExceptions] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [escalationNotes, setEscalationNotes] = useState("");
  const [escalationUserId, setEscalationUserId] = useState<string>("");

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchData();
      fetchUsers();
    }
  }, [selectedBusiness]);

  const fetchData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await ExceptionQueueService.getExceptionQueue(selectedBusiness.id);
      if (response.data.result === "success") {
        setExceptions(response.data.exceptions || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch exception queue",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await UsersService.getAllUsers();
      if (response.data.result === "success") {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleAssign = async () => {
    if (!selectedException || !assigneeId) return;

    try {
      await ExceptionQueueService.assignException(selectedException.id, parseInt(assigneeId));
      toast({
        title: "Success",
        description: "Exception assigned successfully",
      });
      setIsAssignDialogOpen(false);
      setAssigneeId("");
      setSelectedException(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to assign exception",
        variant: "destructive",
      });
    }
  };

  const handleResolve = async () => {
    if (!selectedException) return;

    try {
      await ExceptionQueueService.resolveException(selectedException.id, {
        status: 'resolved',
        resolutionNotes
      });
      toast({
        title: "Success",
        description: "Exception resolved successfully",
      });
      setIsResolveDialogOpen(false);
      setResolutionNotes("");
      setSelectedException(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resolve exception",
        variant: "destructive",
      });
    }
  };

  const handleEscalate = async () => {
    if (!selectedException || !escalationUserId) return;

    try {
      await ExceptionQueueService.escalateException(selectedException.id, {
        escalationUserId: parseInt(escalationUserId),
        escalationNotes
      });
      toast({
        title: "Success",
        description: "Exception escalated successfully",
      });
      setIsEscalateDialogOpen(false);
      setEscalationNotes("");
      setEscalationUserId("");
      setSelectedException(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to escalate exception",
        variant: "destructive",
      });
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedExceptions.length === 0) return;

    try {
      if (bulkAction === "resolve") {
        await ExceptionQueueService.bulkResolveExceptions(selectedExceptions, {
          status: 'resolved',
          resolutionNotes: 'Bulk resolved'
        });
        toast({
          title: "Success",
          description: `${selectedExceptions.length} exceptions resolved successfully`,
        });
      } else if (bulkAction === "assign" && assigneeId) {
        await Promise.all(
          selectedExceptions.map(id => ExceptionQueueService.assignException(id, parseInt(assigneeId)))
        );
        toast({
          title: "Success",
          description: `${selectedExceptions.length} exceptions assigned successfully`,
        });
      }
      
      setSelectedExceptions([]);
      setBulkAction("");
      setAssigneeId("");
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to perform bulk action",
        variant: "destructive",
      });
    }
  };

  const openViewDialog = (exception: ExceptionRecord) => {
    setSelectedException(exception);
    setIsViewDialogOpen(true);
  };

  const openAssignDialog = (exception: ExceptionRecord) => {
    setSelectedException(exception);
    setIsAssignDialogOpen(true);
  };

  const openResolveDialog = (exception: ExceptionRecord) => {
    setSelectedException(exception);
    setIsResolveDialogOpen(true);
  };

  const openEscalateDialog = (exception: ExceptionRecord) => {
    setSelectedException(exception);
    setIsEscalateDialogOpen(true);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants = {
      critical: "bg-red-50 text-red-700 border-red-200",
      high: "bg-orange-50 text-orange-700 border-orange-200",
      medium: "bg-yellow-50 text-yellow-700 border-yellow-200",
      low: "bg-blue-50 text-blue-700 border-blue-200"
    };

    return (
      <Badge className={variants[severity as keyof typeof variants] || variants.low}>
        {getSeverityIcon(severity)}
        <span className="ml-1 capitalize">{severity}</span>
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      resolved: "bg-green-50 text-green-700 border-green-200",
      in_review: "bg-blue-50 text-blue-700 border-blue-200",
      escalated: "bg-purple-50 text-purple-700 border-purple-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200"
    };

    const icons = {
      resolved: <CheckCircle className="h-4 w-4" />,
      in_review: <Clock className="h-4 w-4" />,
      escalated: <ArrowUp className="h-4 w-4" />,
      pending: <AlertTriangle className="h-4 w-4" />
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.pending}>
        {icons[status as keyof typeof icons] || icons.pending}
        <span className="ml-1 capitalize">{status.replace('_', ' ')}</span>
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredExceptions = exceptions.filter(exception => {
    const matchesSearch = exception.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exception.exceptionType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exception.documentUpload?.fileName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || exception.status === statusFilter;
    const matchesSeverity = severityFilter === "all" || exception.severity === severityFilter;
    const matchesCategory = categoryFilter === "all" || exception.exceptionCategory === categoryFilter;
    const matchesAssignee = assigneeFilter === "all" || 
                           (assigneeFilter === "unassigned" && !exception.assignedTo) ||
                           exception.assignedTo?.toString() === assigneeFilter;
    
    return matchesSearch && matchesStatus && matchesSeverity && matchesCategory && matchesAssignee;
  });

  const columns = [
    {
      id: "select",
      header: ({ table }: any) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
            if (value) {
              setSelectedExceptions(filteredExceptions.map(exc => exc.id));
            } else {
              setSelectedExceptions([]);
            }
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }: any) => (
        <Checkbox
          checked={selectedExceptions.includes(row.original.id)}
          onCheckedChange={(value) => {
            if (value) {
              setSelectedExceptions(prev => [...prev, row.original.id]);
            } else {
              setSelectedExceptions(prev => prev.filter(id => id !== row.original.id));
            }
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "exceptionType",
      header: "Exception",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium capitalize">{row.getValue("exceptionType").replace('_', ' ')}</div>
            <div className="text-sm text-muted-foreground">
              {row.original.documentUpload?.fileName || 'No document'}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "exceptionCategory",
      header: "Category",
      cell: ({ row }: any) => (
        <Badge variant="outline" className="capitalize">
          {row.getValue("exceptionCategory")}
        </Badge>
      ),
    },
    {
      accessorKey: "severity",
      header: "Severity",
      cell: ({ row }: any) => getSeverityBadge(row.getValue("severity")),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => getStatusBadge(row.getValue("status")),
    },
    {
      accessorKey: "assignedTo",
      header: "Assigned To",
      cell: ({ row }: any) => {
        const assignedUser = row.original.assignedUser;
        return assignedUser ? (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{assignedUser.firstName} {assignedUser.lastName}</span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Unassigned</span>
        );
      },
    },
    {
      accessorKey: "dateCreated",
      header: "Created",
      cell: ({ row }: any) => formatDate(row.getValue("dateCreated")),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openViewDialog(row.original)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.original.status === 'pending' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openAssignDialog(row.original)}
            >
              <UserCheck className="h-4 w-4" />
            </Button>
          )}
          {(row.original.status === 'pending' || row.original.status === 'in_review') && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openResolveDialog(row.original)}
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEscalateDialog(row.original)}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const statusCounts = {
    all: exceptions.length,
    pending: exceptions.filter(e => e.status === 'pending').length,
    in_review: exceptions.filter(e => e.status === 'in_review').length,
    resolved: exceptions.filter(e => e.status === 'resolved').length,
    escalated: exceptions.filter(e => e.status === 'escalated').length,
  };

  const severityCounts = {
    critical: exceptions.filter(e => e.severity === 'critical').length,
    high: exceptions.filter(e => e.severity === 'high').length,
    medium: exceptions.filter(e => e.severity === 'medium').length,
    low: exceptions.filter(e => e.severity === 'low').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Exception Queue</h1>
          <p className="text-muted-foreground">
            Manage and resolve document processing exceptions
          </p>
        </div>
      </div>

      {/* Status Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <Card key={status} className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setStatusFilter(status)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground capitalize">
                    {status === 'all' ? 'Total' : status.replace('_', ' ')}
                  </p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
                {status !== 'all' && getStatusBadge(status).props.children[0]}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Severity Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Severity Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(severityCounts).map(([severity, count]) => (
              <div key={severity} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  {getSeverityIcon(severity)}
                  <span className="capitalize font-medium">{severity}</span>
                </div>
                <span className="text-lg font-bold">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search exceptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="status-filter">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="severity-filter">Severity</Label>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All severities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category-filter">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="extraction">Extraction</SelectItem>
                  <SelectItem value="classification">Classification</SelectItem>
                  <SelectItem value="validation">Validation</SelectItem>
                  <SelectItem value="posting">Posting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="assignee-filter">Assignee</Label>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All assignees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignees</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedExceptions.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedExceptions.length} exception(s) selected
              </span>
              <div className="flex items-center gap-2">
                <Select value={bulkAction} onValueChange={setBulkAction}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Bulk actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assign">Assign</SelectItem>
                    <SelectItem value="resolve">Resolve</SelectItem>
                  </SelectContent>
                </Select>
                {bulkAction === "assign" && (
                  <Select value={assigneeId} onValueChange={setAssigneeId}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.firstName} {user.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button 
                  onClick={handleBulkAction}
                  disabled={!bulkAction || (bulkAction === "assign" && !assigneeId)}
                  variant="outline"
                >
                  Apply
                </Button>
                <Button 
                  onClick={() => setSelectedExceptions([])}
                  variant="ghost"
                  size="sm"
                >
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exceptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Exceptions ({filteredExceptions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredExceptions}
              section="exception-queue"
              isLoading={loading}
            />
          )}
        </CardContent>
      </Card>

      {/* View Exception Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Exception Details</DialogTitle>
            <DialogDescription>
              View exception information and processing details
            </DialogDescription>
          </DialogHeader>
          {selectedException && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Exception Type</Label>
                  <p className="text-sm capitalize">{selectedException.exceptionType.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label>Category</Label>
                  <p className="text-sm capitalize">{selectedException.exceptionCategory}</p>
                </div>
                <div>
                  <Label>Severity</Label>
                  <div className="mt-1">{getSeverityBadge(selectedException.severity)}</div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedException.status)}</div>
                </div>
                <div>
                  <Label>Created</Label>
                  <p className="text-sm">{formatDate(selectedException.dateCreated)}</p>
                </div>
                <div>
                  <Label>Document</Label>
                  <p className="text-sm">{selectedException.documentUpload?.fileName || 'No document'}</p>
                </div>
              </div>
              {selectedException.description && (
                <div>
                  <Label>Description</Label>
                  <p className="text-sm mt-1 p-3 bg-muted rounded-md">{selectedException.description}</p>
                </div>
              )}
              {selectedException.assignedUser && (
                <div>
                  <Label>Assigned To</Label>
                  <p className="text-sm">{selectedException.assignedUser.firstName} {selectedException.assignedUser.lastName}</p>
                  <p className="text-xs text-muted-foreground">{selectedException.assignedUser.email}</p>
                </div>
              )}
              {selectedException.resolutionNotes && (
                <div>
                  <Label>Resolution Notes</Label>
                  <p className="text-sm mt-1 p-3 bg-muted rounded-md">{selectedException.resolutionNotes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Exception</DialogTitle>
            <DialogDescription>
              Assign this exception to a team member for review
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="assignee">Assign To</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssign} disabled={!assigneeId}>
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={isResolveDialogOpen} onOpenChange={setIsResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Exception</DialogTitle>
            <DialogDescription>
              Mark this exception as resolved and add resolution notes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="resolution-notes">Resolution Notes</Label>
              <Textarea
                id="resolution-notes"
                placeholder="Describe how this exception was resolved..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsResolveDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleResolve}>
                Resolve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={isEscalateDialogOpen} onOpenChange={setIsEscalateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalate Exception</DialogTitle>
            <DialogDescription>
              Escalate this exception to a senior team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="escalation-user">Escalate To</Label>
              <Select value={escalationUserId} onValueChange={setEscalationUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select senior team member" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(user => user.role === 'admin' || user.role === 'manager').map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="escalation-notes">Escalation Notes</Label>
              <Textarea
                id="escalation-notes"
                placeholder="Explain why this exception needs escalation..."
                value={escalationNotes}
                onChange={(e) => setEscalationNotes(e.target.value)}
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEscalateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEscalate} disabled={!escalationUserId}>
                Escalate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 